/**
 * AlinqClient — the agent SDK that links an agent to the relay and runs features
 * over the end-to-end encrypted channel, with trust-tier enforcement.
 *
 * Responsibilities:
 *  - connect(): authenticate to the relay (DID challenge → signature).
 *  - link(card, tier): record a peer + trust tier and the static ECDHE key.
 *  - openSession(peer): a forward-secret handshake (capability.hello/ack carrying
 *    fresh ephemeral X25519 keys) → a per-session key. A leaked session key never
 *    exposes the static identity or other sessions.
 *  - send/onMessage: sealed send/receive; refuses any action above the relationship
 *    tier, both outgoing and incoming (fail closed).
 *  - scheduleMatch(): zero-knowledge scheduling via PSI over the channel.
 *
 * Transport is injected so the same client runs over real WebSockets or an
 * in-memory wiring in tests.
 */
import type {
  AgentCard,
  DID,
  LinkPayload,
  Message,
  Payload,
  RelayEnvelope,
  RelayServerMessage,
  TrustTier,
} from "@alinq/proto";
import { isActionAllowed, isApplicationPayloadType } from "@alinq/proto";
import {
  b64uDecode,
  b64uEncode,
  deriveSessionKey,
  newKeyAgreementKeypair,
  open,
  seal,
  sign,
  type Identity,
} from "./crypto.js";
import { blind, finalize, randomScalar, respond } from "./psi.js";

export interface ClientTransport {
  send(message: unknown): void;
  onMessage(handler: (message: RelayServerMessage) => void): void;
  close(): void;
}

export type MessageHandler = (from: DID, payload: Message) => void;
export type MatchResultHandler = (from: DID, sessionId: string, intersection: string[]) => void;

interface Relationship {
  peerDID: DID;
  tier: TrustTier;
  staticKey: Uint8Array;
  sessionKey?: Uint8Array;
  myEphemeralPriv?: Uint8Array;
  onSessionOpen?: () => void;
}

interface PendingMatch {
  a: bigint;
  mySlots: string[];
  resolve: (intersection: string[]) => void;
}

export class AlinqClient {
  private readonly relationships = new Map<DID, Relationship>();
  private readonly handlers: MessageHandler[] = [];
  private readonly matchHandlers: MatchResultHandler[] = [];
  private readonly pendingMatches = new Map<string, PendingMatch>();
  private availability: string[] = [];
  private authResolve?: () => void;
  private connected = false;
  private unknownSenderResolver?: (did: DID) => Promise<AgentCard | undefined>;

  constructor(
    private readonly identity: Identity,
    private readonly card: AgentCard,
    private readonly transport: ClientTransport,
    private readonly random: { scalar: () => bigint; sessionId: () => string } = {
      scalar: randomScalar,
      sessionId: () => b64uEncode(newKeyAgreementKeypair().pub).slice(0, 16),
    },
  ) {
    this.transport.onMessage((m) => this.onServerMessage(m));
  }

  /** Authenticate to the relay; resolves on auth.ok. */
  connect(): Promise<void> {
    return new Promise((resolve) => {
      this.authResolve = resolve;
      if (this.connected) resolve();
    });
  }

  /** Record a peer and the trust tier, and precompute the static ECDHE key. */
  link(peerCard: AgentCard, tier: TrustTier): void {
    const staticKey = deriveSessionKey(this.identity.kaPriv, b64uDecode(peerCard.publicKeys.keyAgreement));
    this.relationships.set(peerCard.did, { peerDID: peerCard.did, tier, staticKey });
  }

  /** Set this agent's free time slots (used as the PSI input set). */
  setAvailability(slots: string[]): void {
    this.availability = [...slots];
  }

  onMessage(handler: MessageHandler): void {
    this.handlers.push(handler);
  }

  onMatchResult(handler: MatchResultHandler): void {
    this.matchHandlers.push(handler);
  }

  /**
   * Resolver for first-contact key discovery: when a message arrives from a DID we
   * have no relationship with, this is called to fetch that DID's public card (e.g.
   * from the directory) so we can derive the static key and decrypt. A provisional
   * tier-0 relationship is created; the real tier is set when a link is established.
   */
  setUnknownSenderResolver(resolver: (did: DID) => Promise<AgentCard | undefined>): void {
    this.unknownSenderResolver = resolver;
  }

  /** Initiator: open a forward-secret session with a peer. Resolves when ready. */
  openSession(peerDID: DID): Promise<void> {
    const rel = this.requireRel(peerDID);
    const eph = newKeyAgreementKeypair();
    rel.myEphemeralPriv = eph.priv;
    const hello: Payload = { type: "capability.hello", card: this.card, ephemeralKey: b64uEncode(eph.pub) };
    return new Promise((resolve) => {
      // Register the resolver BEFORE sending, so a synchronous transport that
      // round-trips the ack inline still resolves (no lost-wakeup race).
      rel.onSessionOpen = resolve;
      this.sealAndSend(rel, rel.staticKey, hello);
    });
  }

  /**
   * Initiator: run a zero-knowledge scheduling match against a peer's calendar.
   * Resolves with the common free slots only. Requires an open session and tier >= 1.
   */
  scheduleMatch(peerDID: DID): Promise<string[]> {
    const rel = this.requireRel(peerDID);
    this.assertTier("psi.round1", rel.tier);
    if (!rel.sessionKey) throw new Error("open a session before scheduling");
    const a = this.random.scalar();
    const sessionId = this.random.sessionId();
    const mySlots = [...this.availability];
    const blindedPoints = blind(mySlots, a);
    return new Promise((resolve) => {
      this.pendingMatches.set(sessionId, { a, mySlots, resolve });
      this.sendPayload(peerDID, { type: "psi.round1", sessionId, blindedPoints });
    });
  }

  /** Send an application payload to a peer (sealed under the session key, tier-checked). */
  sendPayload(peerDID: DID, payload: Payload): void {
    const rel = this.requireRel(peerDID);
    this.assertTier(payload.type, rel.tier);
    const key = rel.sessionKey ?? rel.staticKey;
    this.sealAndSend(rel, key, payload);
  }

  /**
   * Send a connection-management message (link.*). Not tier-gated — authorized by
   * the pairing code and the co-signature, since a link is what establishes the tier.
   */
  sendLink(peerDID: DID, payload: LinkPayload): void {
    const rel = this.requireRel(peerDID);
    const key = rel.sessionKey ?? rel.staticKey;
    this.sealAndSend(rel, key, payload);
  }

  /** Update the recorded trust tier for a peer (e.g. after a link is accepted or downgraded). */
  setTier(peerDID: DID, tier: TrustTier): void {
    this.requireRel(peerDID).tier = tier;
  }

  /** The current peers and their tiers (for a network view). */
  peers(): Array<{ peerDID: DID; tier: TrustTier; sessionOpen: boolean }> {
    return [...this.relationships.values()].map((r) => ({
      peerDID: r.peerDID,
      tier: r.tier,
      sessionOpen: r.sessionKey !== undefined,
    }));
  }

  /** Whether a relationship exists with a peer. */
  hasPeer(peerDID: DID): boolean {
    return this.relationships.has(peerDID);
  }

  close(): void {
    this.transport.close();
  }

  // --- internals ------------------------------------------------------------

  private requireRel(peerDID: DID): Relationship {
    const rel = this.relationships.get(peerDID);
    if (!rel) throw new Error(`no relationship with ${peerDID}`);
    return rel;
  }

  private assertTier(type: string, tier: TrustTier): void {
    if (isApplicationPayloadType(type) && !isActionAllowed(type, tier)) {
      throw new Error(`action '${type}' requires a higher trust tier than ${tier}`);
    }
  }

  private sealAndSend(rel: Relationship, key: Uint8Array, payload: Message): void {
    const { nonce, ciphertext } = seal(key, payload);
    const env: RelayEnvelope = { type: "envelope", from: this.identity.did, to: rel.peerDID, nonce, ciphertext };
    this.transport.send(env);
  }

  private onServerMessage(msg: RelayServerMessage): void {
    switch (msg.type) {
      case "auth.challenge": {
        const sig = b64uEncode(sign(b64uDecode(msg.nonce), this.identity.signPriv));
        this.transport.send({ type: "auth.response", did: this.identity.did, signature: sig });
        return;
      }
      case "auth.ok": {
        this.connected = true;
        this.authResolve?.();
        return;
      }
      case "envelope":
        void this.onEnvelope(msg);
        return;
      default:
        return; // error/unknown — ignore at this layer
    }
  }

  private async onEnvelope(env: RelayEnvelope): Promise<void> {
    let rel = this.relationships.get(env.from);
    if (!rel) {
      // First contact: try to discover the sender's public card to derive the key.
      if (!this.unknownSenderResolver) return;
      const card = await this.unknownSenderResolver(env.from);
      if (!card) return;
      this.link(card, 0); // provisional tier 0 until a link is established
      rel = this.relationships.get(env.from);
      if (!rel) return;
    }
    const payload = this.decrypt(rel, env);
    if (payload === undefined) return; // could not authenticate/decrypt — drop

    // Fail closed: refuse any application action above the relationship tier.
    if (isApplicationPayloadType(payload.type) && !isActionAllowed(payload.type, rel.tier)) {
      this.sendPayload(env.from, {
        type: "error",
        code: "tier.refused",
        message: `action '${payload.type}' requires a higher trust tier`,
      });
      return;
    }

    this.handleInbound(rel, env.from, payload);
    for (const h of this.handlers) h(env.from, payload);
  }

  /** Try the session key first, then the static key (AEAD authenticates, so a wrong key fails cleanly). */
  private decrypt(rel: Relationship, env: RelayEnvelope): Message | undefined {
    if (rel.sessionKey) {
      try {
        return open<Message>(rel.sessionKey, env.nonce, env.ciphertext);
      } catch {
        /* fall through to the static (handshake) key */
      }
    }
    try {
      return open<Message>(rel.staticKey, env.nonce, env.ciphertext);
    } catch {
      return undefined;
    }
  }

  private handleInbound(rel: Relationship, from: DID, payload: Message): void {
    switch (payload.type) {
      case "capability.hello": {
        // Responder: derive the session key from our fresh ephemeral + their ephemeral.
        const eph = newKeyAgreementKeypair();
        rel.sessionKey = deriveSessionKey(eph.priv, b64uDecode(payload.ephemeralKey));
        const ack: Payload = { type: "capability.ack", card: this.card, ephemeralKey: b64uEncode(eph.pub) };
        this.sealAndSend(rel, rel.staticKey, ack);
        return;
      }
      case "capability.ack": {
        // Initiator: complete the session key from our stored ephemeral + their ephemeral.
        if (rel.myEphemeralPriv) {
          rel.sessionKey = deriveSessionKey(rel.myEphemeralPriv, b64uDecode(payload.ephemeralKey));
          rel.myEphemeralPriv = undefined;
          rel.onSessionOpen?.();
          rel.onSessionOpen = undefined;
        }
        return;
      }
      case "psi.round1": {
        const b = this.random.scalar();
        const r2 = respond(payload.blindedPoints, this.availability, b);
        this.sendPayload(from, {
          type: "psi.round2",
          sessionId: payload.sessionId,
          doubleBlinded: r2.doubleBlinded,
          blindedReply: r2.blindedReply,
        });
        return;
      }
      case "psi.round2": {
        const pending = this.pendingMatches.get(payload.sessionId);
        if (!pending) return;
        const intersection = finalize(
          { doubleBlinded: payload.doubleBlinded, blindedReply: payload.blindedReply },
          pending.a,
          pending.mySlots,
        );
        this.pendingMatches.delete(payload.sessionId);
        // Share the result so the responder also learns the common slots.
        this.sendPayload(from, { type: "psi.result", sessionId: payload.sessionId, intersection });
        pending.resolve(intersection);
        return;
      }
      case "psi.result": {
        for (const h of this.matchHandlers) h(from, payload.sessionId, payload.intersection);
        return;
      }
      default:
        return;
    }
  }
}
